<template>
  <b-modal
    :active="active"
    :can-cancel="isUploading ? [] : ['escape', 'outside']"
    has-modal-card
    @close="$emit('cancel')"
  >
    <div class="modal-card totem-preview-card">
      <header class="modal-card-head">
        <p class="modal-card-title has-text-centered">{{ title }}</p>
      </header>

      <section class="modal-card-body">
        <p class="totem-preview-description">{{ description }}</p>

        <div class="totem-preview-list">
          <div
            class="totem-preview-item"
            v-for="totem in totems"
            :key="totem.key"
          >
            <div class="totem-frame" :class="`totem-frame--${totem.key}`">
              <div class="totem-screen" :style="totem.screenStyle">
                <div class="totem-app">
                  <div
                    class="totem-app-bar totem-app-header"
                    :style="totem.headerStyle"
                  >
                    <span class="totem-dot" :style="accentStyle"></span>
                    <span class="totem-title-group">
                      <span class="totem-bar totem-bar--title"></span>
                      <span class="totem-bar totem-bar--subtitle"></span>
                    </span>
                    <span class="totem-dot" :style="accentStyle"></span>
                  </div>

                  <div class="totem-app-body">
                    <span class="totem-scan-frame">
                      <span class="totem-scan-line"></span>
                    </span>
                  </div>

                  <div
                    class="totem-app-bar totem-app-footer"
                    :style="totem.footerStyle"
                  >
                    <span class="totem-dot" :style="accentStyle"></span>
                    <span class="totem-bar totem-bar--total"></span>
                    <span class="totem-pill" :style="accentStyle"></span>
                  </div>
                </div>

                <div class="totem-overlay">
                  <img
                    v-if="imageUrl"
                    :src="imageUrl"
                    class="totem-promo-image"
                    alt=""
                  />
                  <span class="totem-close" :style="totem.closeStyle">
                    <span
                      class="totem-close-glyph"
                      :style="totem.closeGlyphStyle"
                      >&times;</span
                    >
                  </span>
                </div>
              </div>
            </div>

            <div class="totem-stand"></div>
            <p class="totem-caption">{{ totem.label }}</p>
          </div>
        </div>
      </section>

      <footer
        class="modal-card-foot"
        :class="{ 'totem-preview-foot--upload': !isViewMode }"
      >
        <b-button :disabled="isUploading" @click="$emit('cancel')">
          {{ isViewMode ? $t("common.close") : $t("common.cancel") }}
        </b-button>
        <b-button
          v-if="!isViewMode"
          type="is-primary"
          class="totem-preview-confirm"
          :loading="isUploading"
          :disabled="isUploading || !imageUrl"
          @click="$emit('confirm')"
        >
          {{ $t("promotionalImage.confirmUpload") }}
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
const TOTEM_MODELS = [
  {
    key: "horizontal",
    labelKey: "promotionalImage.horizontalTotem",
    screenWidth: 1024,
    screenHeight: 647,
    headerRatio: 0.178,
    footerRatio: 0.232,
    closeRatio: 0.043
  },
  {
    key: "vertical",
    labelKey: "promotionalImage.verticalTotem",
    screenWidth: 702,
    screenHeight: 1024,
    headerRatio: 0.132,
    footerRatio: 0.186,
    closeRatio: 0.08
  }
];

export default {
  name: "PromotionalImagePreviewModal",

  props: {
    active: {
      type: Boolean,
      default: false
    },
    imageUrl: {
      type: String,
      default: ""
    },
    isUploading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: "upload",
      validator: value => ["upload", "view"].indexOf(value) >= 0
    }
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },

    title() {
      return this.isViewMode
        ? this.$t("promotionalImage.promotionalImageTitle")
        : this.$t("promotionalImage.previewTitle");
    },

    description() {
      return this.isViewMode
        ? this.$t("promotionalImage.currentImageDescription")
        : this.$t("promotionalImage.previewDescription");
    },

    accentStyle() {
      return { backgroundColor: this.$primaryColor };
    },

    totems() {
      return TOTEM_MODELS.map(model => ({
        key: model.key,
        label: this.$t(model.labelKey),
        screenStyle: {
          paddingTop: `${(model.screenHeight / model.screenWidth) * 100}%`
        },
        headerStyle: { height: `${model.headerRatio * 100}%` },
        footerStyle: { height: `${model.footerRatio * 100}%` },
        closeStyle: {
          width: `${model.closeRatio * 100}em`,
          height: `${model.closeRatio * 100}em`
        },
        closeGlyphStyle: { fontSize: `${model.closeRatio * 60}em` }
      }));
    }
  }
};
</script>

<style scoped>
.totem-preview-card {
  max-width: 760px;
  width: 100%;
}

.totem-preview-foot--upload {
  justify-content: center;
}

.totem-preview-description {
  margin-bottom: 24px;
  text-align: center;
}

.totem-preview-list {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: center;
}

.totem-preview-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 16px 8px 16px;
}

.totem-frame {
  background-color: #1c1c1e;
  border-radius: 12px;
  padding: 9px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
}

/* 1em = 1% da largura da tela, para o mockup escalar junto com o frame */
.totem-frame--horizontal {
  width: 366px;
  font-size: 3.66px;
}

.totem-frame--vertical {
  width: 232px;
  font-size: 2.32px;
}

.totem-screen {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: #e9e9e9;
  border-radius: 5px;
}

.totem-app {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
}

.totem-app-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 0 4em;
  background-color: #133b23;
}

.totem-app-header {
  border-bottom-left-radius: 5em;
  border-bottom-right-radius: 5em;
}

.totem-app-footer {
  border-top-left-radius: 5em;
  border-top-right-radius: 5em;
}

.totem-app-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.totem-dot {
  width: 9em;
  height: 9em;
  border-radius: 50%;
  flex-shrink: 0;
}

.totem-title-group {
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 3em;
  margin-right: 3em;
}

.totem-bar {
  display: block;
  border-radius: 2em;
  background-color: rgba(255, 255, 255, 0.75);
}

.totem-bar--title {
  width: 42%;
  height: 3.4em;
}

.totem-bar--subtitle {
  width: 26%;
  height: 2.2em;
  margin-top: 2em;
  background-color: rgba(255, 255, 255, 0.4);
}

.totem-bar--total {
  width: 20%;
  height: 3.4em;
}

.totem-pill {
  width: 26em;
  height: 8em;
  border-radius: 2.5em;
  flex-shrink: 0;
}

.totem-scan-frame {
  position: relative;
  display: block;
  width: 56%;
  height: 34%;
  border: 1em solid rgba(0, 0, 0, 0.28);
  border-radius: 2em;
}

.totem-scan-line {
  position: absolute;
  top: 50%;
  left: 6%;
  width: 88%;
  height: 0.8em;
  background-color: #d0342c;
}

.totem-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.45);
}

.totem-promo-image {
  max-width: 91%;
  max-height: 92%;
  border-radius: 6px;
  object-fit: contain;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
}

.totem-close {
  position: absolute;
  top: 3.5%;
  right: 2.5%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #133b23;
}

.totem-close-glyph {
  color: #ffffff;
  line-height: 1;
}

.totem-stand {
  width: 60px;
  height: 10px;
  background-color: #1c1c1e;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

.totem-caption {
  margin-top: 10px;
  font-weight: 600;
}

.totem-preview-confirm {
  margin-left: 10px;
}
</style>
